import React from "react";
import { motion } from "framer-motion";
import { MapPin, Sparkles, Building2, Trees } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const STATES = [
  {
    code: "NJ",
    name: "New Jersey",
    icon: Trees,
    es: "Servicio en toda el área metropolitana de NJ — hogares, apartamentos y oficinas.",
    en: "Service across the entire NJ metro — homes, apartments and offices.",
  },
  {
    code: "NY",
    name: "New York",
    icon: Building2,
    es: "Atendemos los cinco condados de NY y el área metropolitana.",
    en: "We cover all five NY boroughs and the metro area.",
  },
];

export default function Areas() {
  const { t, lang } = useI18n();
  return (
    <section id="areas" data-testid="areas-section" className="relative py-24 sm:py-32 overflow-hidden bg-white/[0.02]">
      <div className="glow-blob bg-[#EC008C] w-[420px] h-[420px] top-10 -left-32 opacity-25" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="max-w-3xl mb-14">
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FF1F87]">
            {t.areas.eyebrow}
          </span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
            {t.areas.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-2xl">{t.areas.sub}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STATES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                data-testid={`state-card-${s.code.toLowerCase()}`}
                className="group relative bg-[#0A0A0A] border border-white/10 hover:border-[#EC008C]/60 rounded-3xl p-8 lg:p-10 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_20px_70px_-20px_rgba(236,0,140,0.5)]"
              >
                <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#EC008C]/10 blur-3xl group-hover:bg-[#EC008C]/25 transition-colors" />

                <div className="relative flex items-start justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#FF66B3]" />
                  </div>
                  <div className="font-display text-7xl lg:text-8xl font-black chrome-text leading-none opacity-80 group-hover:pink-text transition-colors">
                    {s.code}
                  </div>
                </div>

                <div className="relative mt-8">
                  <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                    <span className="pink-text">{s.name}</span>
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
                    {lang === "es" ? s.es : s.en}
                  </p>

                  <div className="mt-7 flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#FF1F87]">
                    <Sparkles className="w-3.5 h-3.5 sparkle" />
                    {lang === "es" ? "Cobertura completa" : "Full coverage"}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-center gap-3 text-zinc-400 text-sm">
          <MapPin className="w-4 h-4 text-[#FF1F87]" />
          {lang === "es"
            ? "¿No estás seguro si llegamos a tu zona? Llámanos."
            : "Not sure if we serve your area? Give us a call."}
        </div>
      </div>
    </section>
  );
}
